import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { seminarApi, settingsApi } from '../../lib/api';
import { MemberHeader } from '../common/MemberHeader';
import { MemberBreadcrumb } from '../common/MemberBreadcrumb';
import { AdminPreviewBanner } from '../common/AdminPreviewBanner';
import LegalDialog from '../common/LegalDialog';
import { usePublicHeaderConfig } from '../../hooks/usePublicHeaderConfig';
import { formatCurrency } from '../../lib/utils';
import {
    Calendar, MapPin, Clock, Users, ExternalLink, Heart,
    ArrowRight, Award, Building2, GraduationCap, ChevronRight, Shield, FileText, Monitor, Wifi, Tag, CheckCircle
} from 'lucide-react';

const EVENT_CATEGORY_LABELS = {
    fortbildung: 'Fortbildung',
    weiterbildung: 'Weiterbildung',
    ausbildung: 'Ausbildung',
};

const EVENT_FORMAT_CONFIG = {
    praesenz: { label: 'Präsenz', icon: MapPin, color: 'bg-green-50 text-green-700' },
    hybrid: { label: 'Hybrid', icon: Wifi, color: 'bg-purple-50 text-purple-700' },
    online: { label: 'Online', icon: Monitor, color: 'bg-sky-50 text-sky-700' },
};
// removed unused Input import
import { toast } from 'sonner';

function CapacityBadge({ seminar, size = 'sm' }) {
    const status = seminar.capacity_status || 'open';
    const available = typeof seminar.available_seats === 'number'
        ? seminar.available_seats
        : Math.max(0, (seminar.max_participants || 0) - (seminar.current_participants || 0));
    const cfg = {
        full: { label: 'Ausgebucht – Warteliste', cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
        low: { label: `Nur noch ${available} Plätze`, cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
        confirmed: { label: 'Findet statt', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
        open: { label: 'Anmeldung möglich', cls: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
    }[status];
    const sizing = size === 'lg'
        ? 'px-4 py-2.5 text-sm font-semibold rounded-xl'
        : 'px-2 py-0.5 text-[11px] font-semibold rounded-full';
    return (
        <span
            className={`inline-flex items-center gap-1.5 border ${cfg.cls} ${sizing} normal-case tracking-normal whitespace-nowrap`}
            data-testid={`capacity-badge-${seminar.id}`}
            data-status={status}
            title={`${cfg.label} (${seminar.current_participants}/${seminar.max_participants})`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
}

function SeminarCard({ seminar }) {
    const isExternalOrganizer = (seminar.organizer_type || 'bgpr') === 'external';
    // Visual treatment (pink frame, "Externer Anbieter"-Label) is reserved
    // for true external offers. A BGPR-Akademie seminar that merely uses an
    // external registration link still appears as a BGPR seminar.
    const isExternal = isExternalOrganizer;
    const hasExternalRegistration = isExternalOrganizer || !!seminar.external_link;
    const isFull = (seminar.capacity_status === 'full') || (seminar.current_participants >= seminar.max_participants);
    const navigate = useNavigate();

    const goToDetail = () => navigate(`/seminare/${seminar.id}`);

    // Anmeldeschluss kommt vom Backend bereits aufbereitet (registration_deadline_effective + registration_closed)
    const registrationClosed = !!seminar.registration_closed;
    const deadlineRaw = seminar.registration_deadline_effective;
    const deadlineDisplay = deadlineRaw ? formatDate(String(deadlineRaw).slice(0,10)) : '';

    return (
        <div
            role="link"
            tabIndex={0}
            onClick={goToDetail}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDetail(); } }}
            className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all duration-300 flex flex-col h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid={`public-seminar-${seminar.id}`}
            aria-label={`Infos und Anmeldung zu ${seminar.title}`}
        >
            {/* Top accent bar */}
            <div className={`h-1.5 ${isExternal ? 'bg-[#E5007D]' : 'bg-primary'}`} />

            <div className="p-6 flex flex-col flex-1">
                {/* Meta line – clean single row */}
                <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wider font-medium mb-3" data-testid={`public-meta-${seminar.id}`}>
                    <span>{isExternal ? (seminar.organizer_name || 'Externer Anbieter') : 'BGPR-Akademie'}</span>
                    {!isExternal && seminar.event_category && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span>{EVENT_CATEGORY_LABELS[seminar.event_category] || 'Fortbildung'}</span>
                        </>
                    )}
                    {!isExternal && seminar.event_format && seminar.event_format !== 'praesenz' && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span>{(EVENT_FORMAT_CONFIG[seminar.event_format] || EVENT_FORMAT_CONFIG.praesenz).label}</span>
                        </>
                    )}
                    <span className="text-gray-300">·</span>
                    <span>{seminar.ue_count} UE</span>
                    {seminar.course_number && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span className="font-mono" data-testid={`public-course-number-${seminar.id}`}>{seminar.course_number}</span>
                        </>
                    )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                    {seminar.title}
                </h3>
                {seminar.subtitle && (
                    <p className="text-sm text-gray-500 mb-2" data-testid={`public-subtitle-${seminar.id}`}>{seminar.subtitle}</p>
                )}

                {seminar.description && (
                    <div
                        className="legal-html text-sm text-gray-600 mb-4 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: seminar.description }}
                    />
                )}

                {/* Info grid */}
                <div className="space-y-2 text-sm text-gray-600 mb-5">
                    {seminar.date_mode === 'range' && seminar.date_range_start ? (
                        <div className="flex items-center gap-2.5">
                            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span>{formatDate(seminar.date_range_start)} &ndash; {formatDate(seminar.date_range_end)}</span>
                        </div>
                    ) : (() => {
                        const dates = seminar.dates?.length
                            ? seminar.dates
                            : (seminar.date ? [{ date: seminar.date, time_start: seminar.time_start, time_end: seminar.time_end }] : []);
                        return dates.map((d, i) => (
                            <div key={i} className="flex items-center gap-2.5">
                                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span>{formatDate(d.date)}, {d.time_start}&ndash;{d.time_end} Uhr</span>
                            </div>
                        ));
                    })()}
                    <div className="flex items-center gap-2.5">
                        <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{seminar.location}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span>{seminar.current_participants}/{seminar.max_participants} Teilnehmer</span>
                    </div>
                    {deadlineDisplay && !isExternal && (
                        <div className={`flex items-center gap-2.5 ${registrationClosed ? 'text-gray-400' : 'text-amber-700'}`}>
                            <Clock className="h-4 w-4 flex-shrink-0" />
                            <span>
                                {registrationClosed
                                    ? <>Anmeldeschluss war am {deadlineDisplay}</>
                                    : <>Anmeldung bis {deadlineDisplay} möglich</>}
                            </span>
                        </div>
                    )}
                </div>

                {/* Footer block – pinned to bottom by mt-auto */}
                <div className="mt-auto pt-5">
                    {/* Status row above the divider — capacity badge gets its
                        own visual line for non-external seminars so it stops
                        crowding the price/button row. */}
                    {!hasExternalRegistration && (
                        <div className="mb-4 flex items-center" data-testid={`public-status-row-${seminar.id}`}>
                            <CapacityBadge seminar={seminar} size="lg" />
                        </div>
                    )}

                    {/* Price + Action row */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-gray-100">
                        <div className="flex flex-col">
                            <span className="text-2xl font-bold text-gray-900 leading-none">
                                {formatCurrency(seminar.price)}
                            </span>
                            {Number(seminar.price || 0) <= 0 ? (
                                <span className="text-xs text-teal-700 font-medium inline-flex items-center gap-1 mt-1.5" data-testid={`free-hint-${seminar.id}`}>
                                    <CheckCircle className="h-3 w-3" />
                                    Kostenfrei · nur Anmeldung nötig
                                </span>
                            ) : seminar.discounts?.length > 0 && (
                                <span className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1 mt-1.5" data-testid={`discounts-available-${seminar.id}`}>
                                    <Tag className="h-3 w-3" />
                                    {seminar.discounts.length === 1 ? '1 Rabatt' : `${seminar.discounts.length} Rabatte`} verfügbar
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {hasExternalRegistration ? (
                                <a
                                    href={seminar.external_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className={`inline-flex items-center gap-2 px-5 py-3 min-h-[44px] text-white text-sm font-semibold rounded-xl transition-colors ${isExternalOrganizer ? 'bg-[#E5007D] hover:bg-[#C80068]' : 'bg-primary hover:bg-primary/80'}`}
                                    data-testid={`public-external-link-${seminar.id}`}
                                >
                                    Infos & Anmeldung
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            ) : registrationClosed ? (
                                <span
                                    className="inline-flex items-center gap-2 px-5 py-3 min-h-[44px] text-sm font-semibold rounded-xl bg-gray-100 text-gray-500 border border-gray-200"
                                    title={deadlineDisplay ? `Anmeldeschluss war am ${deadlineDisplay}` : 'Anmeldeschluss überschritten'}
                                    data-testid={`public-deadline-closed-${seminar.id}`}
                                >
                                    Anmeldeschluss erreicht
                                </span>
                            ) : (
                                <Link
                                    to={`/seminare/${seminar.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`inline-flex items-center gap-2 px-5 py-3 min-h-[44px] text-sm font-semibold rounded-xl transition-colors ${
                                        isFull
                                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                                            : 'bg-primary text-white hover:bg-primary/80'
                                    }`}
                                    data-testid={`public-info-cta-${seminar.id}`}
                                >
                                    {isFull ? 'Infos & Warteliste' : 'Infos & Anmeldung'}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        let d;
        // Handle DD.MM.YYYY format
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) {
                d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            }
        } else {
            d = new Date(dateStr + 'T00:00:00');
        }
        if (!d || isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
        return dateStr;
    }
}

export default function PublicSeminarsPage() {
    const [seminars, setSeminars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [headerLogo, setHeaderLogo] = useState(null);
    const [legalOpen, setLegalOpen] = useState(null);
    const headerCfg = usePublicHeaderConfig();
    const { isAuthenticated, user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();

    // Admins/Super-Admins sehen die öffentliche Ansicht der Seminarseite – sie sind
    // nicht automatisch im Akademie-Portal eingeloggt, nur weil sie ein Admin-Konto haben.
    const showAsMember = isAuthenticated && !isAdmin();

    const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    useEffect(() => {
        loadSeminars();
        const img = new Image();
        img.onload = () => setHeaderLogo(`${BACKEND_URL}/api/settings/logos/header`);
        img.onerror = () => setHeaderLogo(null);
        img.src = `${BACKEND_URL}/api/settings/logos/header`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSeminars = async () => {
        try {
            const response = await seminarApi.getPublic();
            setSeminars(response.data);
        } catch (error) {
            console.error('Fehler beim Laden:', error);
        } finally {
            setLoading(false);
        }
    };

    const bgprSeminars = seminars.filter(s => (s.organizer_type || 'bgpr') === 'bgpr' && s.event_category !== 'ausbildung');
    const ausbildungSeminars = seminars.filter(s => (s.organizer_type || 'bgpr') === 'bgpr' && s.event_category === 'ausbildung');
    const externalSeminars = seminars.filter(s => (s.organizer_type || 'bgpr') === 'external');

    // Hero/Hintergrundbild greift nur auf der öffentlichen Seite – nicht im eingeloggten Portal.
    const useHeroWrapper = !showAsMember && headerCfg.extendTopbar && headerCfg.hasImage;

    return (
        <div className="min-h-screen bg-gray-50">
            <AdminPreviewBanner />
            {showAsMember ? (
                /* Eingeloggte Mitglieder: nur Top-Header + Breadcrumb, kein Hero */
                <>
                    <MemberHeader />
                    <MemberBreadcrumb />
                </>
            ) : (
                /* Wrapper, der bei extendTopbar Header + Hero gemeinsam mit Hintergrundbild verklammert */
                <div
                    className={useHeroWrapper ? 'relative overflow-hidden' : ''}
                    style={useHeroWrapper ? headerCfg.sectionStyle : undefined}
                >
                    {useHeroWrapper && (
                        <>
                            <div className="absolute inset-0 pointer-events-none" style={headerCfg.overlayStyle} aria-hidden="true" />
                            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-black/0 to-black/40" aria-hidden="true" />
                        </>
                    )}
                    {/* Header for anonymous visitors */}
                    <header className={`relative ${useHeroWrapper ? 'bg-transparent' : 'bg-secondary'}`}>
                        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {headerLogo ? (
                                        <img src={headerLogo} alt="BGPR-Akademie" className="h-10 w-auto object-contain" />
                                    ) : (
                                        <div className="p-2.5 bg-primary rounded-xl">
                                            <Heart className="h-6 w-6 text-white" />
                                        </div>
                                    )}
                                    <div>
                                        <h1 className="text-xl font-bold text-white drop-shadow">BGPR-Akademie</h1>
                                        <p className="text-xs text-gray-200 drop-shadow">Berliner Gesellschaft für Prävention und Rehabilitation</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Hero */}
                    <section
                        className={`relative ${useHeroWrapper ? 'bg-transparent pt-10 pb-16 sm:pt-14 sm:pb-24' : 'bg-secondary py-16 sm:py-24'} min-h-[260px] sm:min-h-[340px] overflow-hidden flex items-center`}
                        style={useHeroWrapper ? undefined : headerCfg.sectionStyle}
                        data-testid="public-hero-section"
                    >
                        {headerCfg.hasImage && !headerCfg.extendTopbar && (
                            <>
                                <div className="absolute inset-0 pointer-events-none" style={headerCfg.overlayStyle} aria-hidden="true" />
                                <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-black/0 to-black/40" aria-hidden="true" />
                            </>
                        )}
                        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center w-full">
                            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 [text-shadow:_0_2px_8px_rgb(0_0_0_/_45%)]" data-testid="public-hero-title">
                                {headerCfg.title || 'Fort- & Weiterbildungen'}
                            </h2>
                            <p className="text-gray-100 text-base sm:text-lg max-w-2xl mx-auto [text-shadow:_0_1px_4px_rgb(0_0_0_/_45%)]" data-testid="public-hero-subtitle">
                                {headerCfg.subtitle || 'Entdecken Sie unsere Seminare der BGPR-Akademie sowie Angebote externer Partner für Herzgruppenleiter.'}
                            </p>
                        </div>
                    </section>
                </div>
            )}

            {/* Main Content */}
            <main className={`max-w-6xl mx-auto px-4 sm:px-6 ${showAsMember ? 'pt-6' : 'pt-10 sm:pt-12'}`}>
                {/* ===== Akademie-Mitgliedschaft (prominent) ===== */}
                {!showAsMember && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-10" data-testid="section-academy-membership">
                    <div className="flex flex-col sm:flex-row">
                        <div className="sm:w-2/3 p-5 sm:p-6 flex items-center gap-4">
                            <div className="p-3 bg-secondary rounded-xl shrink-0">
                                <Shield className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Mitglied in der BGPR-Akademie werden</h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Einmal registrieren – danach Seminare buchen, Bescheinigungen abrufen und Lizenzen verlängern. Alles im persönlichen Profil.
                                </p>
                            </div>
                        </div>
                        <div className="sm:w-1/3 p-5 sm:p-6 flex items-center justify-end gap-3 border-t sm:border-t-0 sm:border-l border-gray-100">
                            <Link
                                to="/teilnehmer/registrieren"
                                className="inline-flex items-center gap-1.5 px-4 py-3 min-h-[44px] bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/80 transition-colors"
                                data-testid="academy-register-link"
                            >
                                Registrieren
                                <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                            <Link
                                to="/teilnehmer/login"
                                className="inline-flex items-center gap-1.5 px-4 py-3 min-h-[44px] border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                                data-testid="academy-login-link"
                            >
                                Anmelden
                            </Link>
                        </div>
                    </div>
                </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <div className="animate-spin h-10 w-10 border-3 border-primary border-t-transparent rounded-full" />
                    </div>
                ) : (
                    <>
                        {/* ===== BGPR-Akademie Sektion ===== */}
                        <section className="mb-14" data-testid="section-bgpr">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2.5 bg-primary rounded-xl">
                                    <Award className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">BGPR-Akademie</h3>
                                    <p className="text-sm text-gray-500">Seminare und Fortbildungen der BGPR</p>
                                </div>
                            </div>
                            {bgprSeminars.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500">
                                        Aktuell sind keine BGPR-Seminare verfügbar.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-6" data-testid="bgpr-seminar-grid">
                                    {bgprSeminars.map((seminar) => (
                                        <SeminarCard key={seminar.id} seminar={seminar} />
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* ===== Ausbildung Sektion ===== */}
                        {ausbildungSeminars.length > 0 && (
                            <section className="mb-16" data-testid="section-ausbildung">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2.5 bg-primary rounded-xl">
                                        <GraduationCap className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Ausbildung zum Herzgruppenleiter</h3>
                                        <p className="text-sm text-gray-500">Werden Sie zertifizierter Herzgruppenleiter der BGPR</p>
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-6" data-testid="ausbildung-seminar-grid">
                                    {ausbildungSeminars.map((seminar) => (
                                        <SeminarCard key={seminar.id} seminar={seminar} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* ===== Externe Anbieter Sektion ===== */}
                        <section className="mb-16" data-testid="section-external">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 bg-[#E5007D] rounded-xl">
                                    <Building2 className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Angebote externer Partner</h3>
                                    <p className="text-sm text-gray-500">Fortbildungen von externen Veranstaltern</p>
                                </div>
                            </div>
                            {/* Hinweis */}
                            <div className="p-4 mb-6 bg-[#FDF0F6] border border-[#E5007D]/30 rounded-xl text-sm text-[#9A0054]" data-testid="external-disclaimer">
                                <p>
                                    <strong>Hinweis:</strong> Die nachfolgend aufgeführten Veranstaltungen werden von externen Anbietern organisiert und durchgeführt.
                                    Für Anmeldung, Inhalte und Durchführung ist ausschließlich der jeweilige Veranstalter verantwortlich.
                                    Die BGPR übernimmt hierfür keine Haftung.
                                </p>
                            </div>
                            {externalSeminars.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                                    <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500">
                                        Aktuell liegen keine externen Angebote vor.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-6" data-testid="external-seminar-grid">
                                    {externalSeminars.map((seminar) => (
                                        <SeminarCard key={seminar.id} seminar={seminar} />
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-secondary mt-16 py-8">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        {headerLogo ? (
                            <img src={headerLogo} alt="BGPR-Akademie" className="h-5 w-auto object-contain" />
                        ) : (
                            <Heart className="h-4 w-4 text-primary" />
                        )}
                        <span className="text-sm font-semibold text-white">BGPR-Akademie</span>
                    </div>
                    <p className="text-xs text-gray-300 mb-3">
                        Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-300">
                        <button type="button" onClick={() => setLegalOpen('imprint')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-imprint">Impressum</button>
                        <span className="text-gray-500 hidden sm:inline">|</span>
                        <button type="button" onClick={() => setLegalOpen('privacy_policy')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-privacy">Datenschutzhinweis</button>
                        <span className="text-gray-500 hidden sm:inline">|</span>
                        <button type="button" onClick={() => setLegalOpen('terms_of_service')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-terms">Nutzungsbedingungen</button>
                        <span className="text-gray-500 hidden sm:inline">|</span>
                        <Link to="/teilnehmer/login" className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-login-link">Anmelden</Link>
                    </div>
                </div>
            </footer>
            <LegalDialog type={legalOpen} open={!!legalOpen} onOpenChange={(v) => !v && setLegalOpen(null)} />
        </div>
    );
}
